import { t } from "@lingui/core/macro";
import { useAtom } from "jotai";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useFragment } from "react-relay";

import { Button } from "@whoosh/design-system/src/components/controls/Button/Button";
import { StandardModal } from "@whoosh/design-system/src/components/overlay/StandardModal/StandardModal";
import {
  VStack,
  Text,
  HStack,
} from "@whoosh/design-system/src/components/primitive";

import { BookingType } from "../../__generated__/BookingDetailsView_Booking.graphql";
import { applyPunchPassModalAtom } from "../../atoms";
import { ApplyPunchPassModalContent } from "./components/ApplyPunchPassModalContent";
import { ApplyPunchPassModalContentFragment } from "./components/ApplyPunchPassModalContent";
import { ApplyPunchPassModalContent_Booking$key } from "./components/__generated__/ApplyPunchPassModalContent_Booking.graphql";
import { useUpdateBookingPunchPass } from "./hooks/useUpdateBookingPunchPass/useUpdateBookingPunchPass";
import { useUpdateRoundPunchPasses } from "./hooks/useUpdateRoundPunchPasses/useUpdateRoundPunchPasses";

export const ApplyPunchPassModal = ({
  bookingRef,
  bookingType,
  membershipTypeId,
}: {
  bookingRef: ApplyPunchPassModalContent_Booking$key | null;
  bookingType: BookingType;
  membershipTypeId: string;
}) => {
  const [applyPunchPassModal, setApplyPunchPassModal] = useAtom(
    applyPunchPassModalAtom
  );

  const isApplyPunchPassModalOpen = applyPunchPassModal !== null;
  const context = applyPunchPassModal;

  const [updateBookingPunchPass, isUpdatingBooking] = useUpdateBookingPunchPass(
    bookingType,
    membershipTypeId
  );
  const [updateRoundPunchPasses, isUpdatingRound] = useUpdateRoundPunchPasses(
    bookingType,
    membershipTypeId
  );

  const [roundApplied, setRoundApplied] = useState<{
    rateId: string | null;
    transportId: string | null;
  } | null>(null);
  const [bookingAppliedId, setBookingAppliedId] = useState<string | null>(null);

  const booking = useFragment(ApplyPunchPassModalContentFragment, bookingRef);

  const roundId = context?.type === "round" ? context.roundId : null;

  const initialRoundApplied = useMemo(() => {
    if (!booking || !roundId) return null;
    const edges = booking.rounds?.edges ?? [];
    for (const edge of edges) {
      if (edge?.node?.id === roundId) {
        const node = edge.node;
        return {
          rateId: node.ratePunchPass?.id ?? null,
          transportId: node.transportationPunchPass?.id ?? null,
        };
      }
    }
    return null;
  }, [booking, roundId]);

  const initialBookingAppliedId = useMemo(() => {
    if (!booking || context?.type !== "booking") return null;
    return booking.appliedPunchPass?.id ?? null;
  }, [booking, context?.type]);

  useEffect(() => {
    if (!context) return;
    if (context.type === "round") {
      setRoundApplied(
        initialRoundApplied ?? { rateId: null, transportId: null }
      );
      setBookingAppliedId(null);
    } else if (context.type === "booking") {
      setBookingAppliedId(initialBookingAppliedId ?? null);
      setRoundApplied(null);
    }
  }, [context, initialRoundApplied, initialBookingAppliedId]);

  const isDirty = useMemo(() => {
    if (!context) return false;
    if (
      context.type === "round" &&
      roundApplied != null &&
      initialRoundApplied != null
    ) {
      return (
        roundApplied.rateId !== initialRoundApplied.rateId ||
        roundApplied.transportId !== initialRoundApplied.transportId
      );
    }
    if (context.type === "booking") {
      return bookingAppliedId !== initialBookingAppliedId;
    }
    return false;
  }, [
    context,
    roundApplied,
    bookingAppliedId,
    initialRoundApplied,
    initialBookingAppliedId,
  ]);

  const resetAndClose = useCallback(() => {
    setApplyPunchPassModal(null);
    setRoundApplied(null);
    setBookingAppliedId(null);
  }, [setApplyPunchPassModal]);

  const handleClose = () => {
    resetAndClose();
  };

  const handleRoundAppliedChange = useCallback(
    (ratePunchPassId: string | null, transportPunchPassId: string | null) => {
      setRoundApplied({
        rateId: ratePunchPassId,
        transportId: transportPunchPassId,
      });
    },
    []
  );

  const handleBookingAppliedChange = useCallback(
    (punchPassId: string | null) => {
      setBookingAppliedId(punchPassId);
    },
    []
  );

  const handleConfirmAndSave = useCallback(() => {
    if (!context) return;
    if (context.type === "booking") {
      const bookingId = context.bookingId;
      updateBookingPunchPass(bookingId, bookingAppliedId, resetAndClose);
    } else if (context.type === "round" && roundApplied != null) {
      updateRoundPunchPasses(
        context.roundId,
        roundApplied.rateId,
        roundApplied.transportId,
        resetAndClose
      );
    }
  }, [
    context,
    roundApplied,
    bookingAppliedId,
    updateBookingPunchPass,
    updateRoundPunchPasses,
    resetAndClose,
  ]);

  const modalSubtitle =
    context?.type === "booking"
      ? t`${context?.ownerName}'s Booking`
      : context?.type === "round"
      ? context.playerName
      : null;

  const isSaving = isUpdatingBooking || isUpdatingRound;

  return (
    <StandardModal
      isOpen={isApplyPunchPassModalOpen}
      onClose={handleClose}
      upperNode={
        <HStack
          alignItems="center"
          justifyContent="center"
          flex={1}
          paddingRight={14}
        >
          <VStack flex={1} alignItems="center">
            <Text variant="mainBaseSemiBold">{t`Punch Passes`}</Text>
            <Text
              variant="mainTiny"
              fontWeight="semibold"
              color="text-secondary"
            >
              {modalSubtitle}
            </Text>
          </VStack>
        </HStack>
      }
      content={
        bookingRef ? (
          <ApplyPunchPassModalContent
            bookingRef={bookingRef}
            context={context}
            roundApplied={
              context?.type === "round"
                ? roundApplied ??
                  initialRoundApplied ?? { rateId: null, transportId: null }
                : null
            }
            bookingAppliedId={
              context?.type === "booking" ? bookingAppliedId ?? null : null
            }
            onRoundAppliedChange={handleRoundAppliedChange}
            onBookingAppliedChange={handleBookingAppliedChange}
          />
        ) : undefined
      }
      footerContent={
        isDirty ? (
          <HStack
            alignItems="center"
            paddingVertical={5}
            borderTopWidth={1.5}
            borderTopColor="surface-dividers"
          >
            <Button
              variant="main"
              size="regular"
              onPress={handleConfirmAndSave}
              style={{ flex: 1 }}
              isLoading={isSaving}
              accessibilityLabel={t`Confirm and Save Button`}
            >{t`Confirm and Save`}</Button>
          </HStack>
        ) : null
      }
    />
  );
};
